"use client";

import { motion } from "framer-motion";

/**
 * Karta 4 — „Działa na każdym urządzeniu"
 * 3 device frames (laptop, tablet, phone) w perspektywie z mini-dashboardem
 * Subtelne sync particles przepływające między urządzeniami (real-time sync)
 */
export function VisualDevices() {
  return (
    <div
      className="absolute inset-0 pointer-events-none flex items-center justify-center"
      aria-hidden
    >
      <div className="relative w-full max-w-[340px]" style={{ height: 130 }}>
        {/* === LAPTOP (back, large) === */}
        <div
          className="absolute bottom-6 left-[18%] z-10"
          style={{ width: 168, height: 110 }}
        >
          {/* Screen frame */}
          <div className="absolute inset-x-0 top-0 h-[100px] rounded-[6px] bg-[#0A0A0F] p-[3px]">
            <div className="w-full h-full rounded-[3px] bg-white overflow-hidden flex">
              {/* Mini sidebar */}
              <div className="w-[18px] h-full bg-[#F4F5F8] flex flex-col items-center pt-1.5 gap-1">
                <div className="w-2 h-2 rounded-sm bg-primary" />
                <div className="w-2 h-2 rounded-sm bg-muted-foreground/30" />
                <div className="w-2 h-2 rounded-sm bg-muted-foreground/30" />
                <div className="w-2 h-2 rounded-sm bg-muted-foreground/30" />
              </div>
              {/* 3-column dashboard */}
              <div className="flex-1 p-1.5 grid grid-cols-3 gap-1">
                <div className="rounded-sm bg-[#F4F5F8]" />
                <div className="rounded-sm bg-[#F4F5F8]" />
                <div className="rounded-sm bg-primary/20" />
                <div className="rounded-sm bg-[#F4F5F8]" />
                <div className="rounded-sm bg-[#F4F5F8]" />
                <div className="rounded-sm bg-[#F4F5F8]" />
              </div>
            </div>
          </div>
          {/* Laptop base */}
          <div className="absolute bottom-0 inset-x-[-8px] h-[6px] bg-[#0A0A0F] rounded-b-[8px]" />
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-6 h-[3px] bg-[#1A1A24] rounded-full" />
        </div>

        {/* === TABLET (middle) === */}
        <div
          className="absolute bottom-2 left-[42%] z-20"
          style={{ width: 76, height: 100 }}
        >
          <div className="w-full h-full rounded-[6px] bg-[#0A0A0F] p-[3px] shadow-lg">
            <div className="w-full h-full rounded-[3px] bg-white overflow-hidden flex flex-col">
              {/* Top bar with icon strip */}
              <div className="h-[10px] bg-[#F4F5F8] flex items-center gap-0.5 px-1">
                <div className="w-1.5 h-1.5 rounded-sm bg-primary" />
                <div className="w-1.5 h-1.5 rounded-sm bg-muted-foreground/30" />
                <div className="w-1.5 h-1.5 rounded-sm bg-muted-foreground/30" />
              </div>
              {/* 2-column dashboard */}
              <div className="flex-1 p-1 grid grid-cols-2 gap-1">
                <div className="rounded-sm bg-[#F4F5F8]" />
                <div className="rounded-sm bg-primary/20" />
                <div className="rounded-sm bg-[#F4F5F8]" />
                <div className="rounded-sm bg-[#F4F5F8]" />
                <div className="rounded-sm bg-[#F4F5F8]" />
                <div className="rounded-sm bg-[#F4F5F8]" />
              </div>
            </div>
          </div>
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#1A1A24]" />
        </div>

        {/* === PHONE (front-right, small) === */}
        <div
          className="absolute bottom-0 right-[18%] z-30"
          style={{ width: 46, height: 92 }}
        >
          <div className="w-full h-full rounded-[8px] bg-[#0A0A0F] p-[2px] shadow-xl">
            <div className="w-full h-full rounded-[6px] bg-white overflow-hidden flex flex-col">
              {/* Notch */}
              <div className="h-[8px] flex items-center justify-center">
                <div className="w-3 h-1 rounded-full bg-[#0A0A0F]" />
              </div>
              {/* Hamburger + content */}
              <div className="flex items-center px-1 gap-0.5">
                <div className="flex flex-col gap-[1px]">
                  <div className="w-2 h-[1px] bg-muted-foreground/40" />
                  <div className="w-2 h-[1px] bg-muted-foreground/40" />
                  <div className="w-2 h-[1px] bg-muted-foreground/40" />
                </div>
              </div>
              {/* 1-column list */}
              <div className="flex-1 p-1 flex flex-col gap-1">
                <div className="rounded-sm bg-primary/20 h-3" />
                <div className="rounded-sm bg-[#F4F5F8] h-3" />
                <div className="rounded-sm bg-[#F4F5F8] h-3" />
                <div className="rounded-sm bg-[#F4F5F8] h-3" />
              </div>
            </div>
          </div>
        </div>

        {/* === SYNC PARTICLES (HTML divs — % w left/top działa, SVG nie wspiera % w cx/cy via framer) === */}
        {/* Laptop → Tablet */}
        {[0, 1].map((i) => (
          <motion.div
            key={`sync-a-${i}`}
            className="absolute w-1.5 h-1.5 rounded-full bg-primary z-40"
            initial={{ opacity: 0 }}
            animate={{
              left: ["20%", "40%", "55%"],
              top: ["44%", "30%", "55%"],
              opacity: [0, 0.7, 0],
            }}
            transition={{
              duration: 2.4,
              repeat: Infinity,
              delay: i * 1.2,
              ease: "easeInOut",
            }}
            aria-hidden
          />
        ))}
        {/* Tablet → Phone */}
        {[0, 1].map((i) => (
          <motion.div
            key={`sync-b-${i}`}
            className="absolute w-1.5 h-1.5 rounded-full bg-primary z-40"
            initial={{ opacity: 0 }}
            animate={{
              left: ["55%", "68%", "80%"],
              top: ["55%", "40%", "75%"],
              opacity: [0, 0.7, 0],
            }}
            transition={{
              duration: 2.4,
              repeat: Infinity,
              delay: i * 1.2 + 0.6,
              ease: "easeInOut",
            }}
            aria-hidden
          />
        ))}
      </div>
    </div>
  );
}
